import type { JsonLd } from 'json-ld-types';

import { SmartCardLocalCacheClient } from '@atlaskit/editor-card-provider';
import { CardClient } from '@atlaskit/link-provider';
import { getStatus } from '@atlaskit/linking-common';
import type { CardStore, EnvironmentsKeys } from '@atlaskit/linking-common';

import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import { expValEquals } from '@confluence/feature-experiments';

type onResolveFn = (data: unknown | undefined) => void;

const deferIdle = (cb: (data: unknown) => void) => {
	if ('requestIdleCallback' in window) {
		requestIdleCallback(cb, { timeout: 5000 });
	} else {
		requestAnimationFrame(cb);
	}
};

export class ConfluenceSmartCardClient extends CardClient {
	onResolve: onResolveFn;
	private readonly editorCardProvider: ConfluenceEditorCardProvider;
	// When cleaning up platform_editor_smartlink_local_cache, remove ? here and in other places in this file
	private readonly smartCardLocalCacheClient?: SmartCardLocalCacheClient;

	constructor(props: { onResolve: onResolveFn; envKey?: EnvironmentsKeys }) {
		const { onResolve, envKey } = props;
		super(envKey);
		this.onResolve = onResolve;

		// Because Card components uses data from client and it's super hard to pass ConfluenceEditorCardProvider to them,
		// we will create a new ConfluenceEditorCardProvider instance here and use it for SSR support.
		this.editorCardProvider = new ConfluenceEditorCardProvider(
			this.envKey as EnvironmentsKeys,
			this.baseUrlOverride,
		);

		if (expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)) {
			this.smartCardLocalCacheClient = SmartCardLocalCacheClient.getInstance();
		} else {
			this.smartCardLocalCacheClient = undefined;
		}
		const ssrProvidersData: {
			[key: string | 'editorCardProvider']: Record<string, JsonLd.Response>;
		} = window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
		const ssrData = ssrProvidersData[this.editorCardProvider.name];
		this.editorCardProvider.setSSRData(ssrData);
	}

	// Called once per page load
	prefetchData(url: string): ReturnType<CardClient['prefetchData']> {
		return super.prefetchData(url).then((data) => {
			// Using deferIdle(requestIdleCallback) to ensure our onResolve callback doesn't interrupt the smart card client.
			// This means there's a chance under heavy browser load that the delay between client (pre)fetch and onResolve invocation will be >1s, but usually it shouldn't be.
			// Given the data schema is unstable, we're setting the type as unknown and expecting individual resolvers to be mindful.
			data && deferIdle(() => this.onResolve(data as unknown));
			return data;
		});
	}

	// Called on load and on click of each smart card link
	async fetchData(url: string, force?: boolean): ReturnType<CardClient['fetchData']> {
		if (
			!force ||
			// Exposure needs to fire regardless of whether the url is in cache
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			(expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true) &&
				this.smartCardLocalCacheClient?.isUrlInCache(url))
		) {
			return this.editorCardProvider
				.getDataAsPromise_DO_NOT_USE_OUTSIDE_MIGRATIONS({
					// It's ok to cast any resourceUrl to inlineCard here, because only URL is important for the request.
					type: 'inlineCard',
					attrs: {
						url,
					},
				})
				.then((data) => {
					data && deferIdle(() => this.onResolve(data as unknown));
					return data;
				});
		}

		return super.fetchData(url, force).then((data) => {
			data && deferIdle(() => this.onResolve(data as unknown));
			return data;
		});
	}

	getInitialCardStoreFromCache = (): CardStore => {
		const initialStateCardStore: CardStore = {};
		const cachedData = this.smartCardLocalCacheClient?.getCache() ?? {};
		Object.entries(cachedData).forEach(([url, value]) => {
			const details = value.data;
			initialStateCardStore[url] = {
				status: getStatus(details),
				details,
				metadataStatus: 'resolved',
			};
		});
		return initialStateCardStore;
	};
}
